import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  type CarFilter,
  type CarSortKey,
  type CarSummary,
  type SortDir,
  getCars,
} from "../api/client";
import FilterSidebar from "../components/FilterSidebar";
import { useAuth } from "../context/AuthContext";
import { useBrandContext } from "../context/BrandContext";
import { usePersistedState } from "../hooks/usePersistedState";
import { filterFromParams, filterToParams } from "../utils/filterUrl";
import {
  COLUMN_WIDTH_MAX,
  COLUMNS,
  sanitizeColumnPrefs,
  sanitizeColumnWidths,
  type ColumnDef,
  type ColumnPrefs,
  type ColumnWidths,
} from "./inventoryColumns";

function CarRow({ car, columns }: { car: CarSummary; columns: readonly ColumnDef[] }) {
  const dimmed =
    car.status === "disappeared" ||
    car.sale_status === "sold" ||
    car.sale_status === "exported";
  return (
    <tr
      className={`border-b hover:bg-gray-50 transition-colors ${dimmed ? "opacity-60" : ""}`}
    >
      {columns.map((col) => (
        // Fixed layout + nowrap + ellipsis: cells truncate to their column's
        // width instead of stretching or wrapping it.
        <td
          key={col.id}
          className={`${col.responsiveCls} ${col.cellCls} overflow-hidden text-ellipsis whitespace-nowrap`.trim()}
        >
          {col.cell(car)}
        </td>
      ))}
    </tr>
  );
}

// Filter ↔ URL roundtrip helpers live in `utils/filterUrl.ts` so every
// analytics tab (Scatter / Depreciation / Efficiency / BestValue / etc.)
// shares the same encoding and the NavBar can carry the search string
// across tab switches.

// Rows-per-page options sit within the API's limit bound (1–200); 60 keeps
// the client default aligned with the API's and the spec's (§2.4.3).
const PAGE_SIZE_OPTIONS = [25, 60, 100, 200] as const;
const DEFAULT_PAGE_SIZE = 60;

function sanitizePageSize(raw: unknown): number | undefined {
  return typeof raw === "number" && (PAGE_SIZE_OPTIONS as readonly number[]).includes(raw)
    ? raw
    : undefined;
}

// Sort defaults mirror the API's (spec §2.4.3): price ascending. Default
// values are kept out of the URL so unsorted links stay canonical.
const DEFAULT_SORT_BY: CarSortKey = "price";
const DEFAULT_SORT_DIR: SortDir = "asc";

const SORT_KEYS: ReadonlySet<string> = new Set(
  COLUMNS.flatMap((col) => (col.sortKey ? [col.sortKey] : [])),
);

export default function Inventory() {
  const { activeBrand, brands } = useBrandContext();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cars, setCars] = useState<CarSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Per-device column visibility: a sparse override map on top of each
  // column's defaultVisible, so future columns pick up their default.
  const [columnPrefs, setColumnPrefs] = usePersistedState<ColumnPrefs>(
    "polestar.inventoryColumns",
    {},
    sanitizeColumnPrefs,
  );
  const [chooserOpen, setChooserOpen] = useState(false);

  // Per-device rows-per-page. An offset computed under the old size points
  // at a meaningless window, so changing it resets to the first page.
  const [pageSize, setPageSize] = usePersistedState<number>(
    "polestar.inventoryPageSize",
    DEFAULT_PAGE_SIZE,
    sanitizePageSize,
  );

  // Per-device column widths (sparse px map; unset columns use defaultWidth).
  const [colWidths, setColWidths] = usePersistedState<ColumnWidths>(
    "polestar.inventoryColumnWidths",
    {},
    sanitizeColumnWidths,
  );

  // Same drag pattern as the FilterSidebar resize handle: listeners attach on
  // mousedown and detach on mouseup; the page cursor is locked and text
  // selection suppressed for the duration of the drag.
  function startColumnResize(e: React.MouseEvent, col: ColumnDef) {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = colWidths[col.id] ?? col.defaultWidth;
    const onMove = (ev: MouseEvent) => {
      const next = Math.max(
        col.minWidth,
        Math.min(COLUMN_WIDTH_MAX, startWidth + (ev.clientX - startX)),
      );
      setColWidths((w) => ({ ...w, [col.id]: next }));
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }

  const filter = useMemo(() => filterFromParams(searchParams), [searchParams]);

  // Sort travels in the URL alongside the filter (shareable view state), but
  // is not part of the filter primitive — filterFromParams ignores it and
  // saved filters never carry it. Unknown values fall back to the default.
  const rawSortBy = searchParams.get("sort_by") ?? "";
  const sortBy: CarSortKey = SORT_KEYS.has(rawSortBy)
    ? (rawSortBy as CarSortKey)
    : DEFAULT_SORT_BY;
  const sortDir: SortDir =
    searchParams.get("sort_dir") === "desc" ? "desc" : DEFAULT_SORT_DIR;

  function writeSortParams(params: URLSearchParams, by: CarSortKey, dir: SortDir) {
    if (by === DEFAULT_SORT_BY) params.delete("sort_by");
    else params.set("sort_by", by);
    if (dir === DEFAULT_SORT_DIR) params.delete("sort_dir");
    else params.set("sort_dir", dir);
  }

  function toggleSort(key: CarSortKey) {
    const nextDir: SortDir =
      key === sortBy ? (sortDir === "asc" ? "desc" : "asc") : DEFAULT_SORT_DIR;
    const next = new URLSearchParams(searchParams);
    writeSortParams(next, key, nextDir);
    setSearchParams(next, { replace: true });
  }

  // "My favourites" is a request option, not a filter axis (§2.4.8) — it is
  // user-scoped, so letting it into the filter primitive would mean saved
  // filters and alert criteria could carry a "my stars" predicate into a
  // context where "my" is undefined. It travels in the URL like sort does.
  // Signing out drops it: the API 401s an anonymous favourites-only request.
  const favouritesOnly = user != null && searchParams.get("favourites") === "1";

  function toggleFavouritesOnly() {
    const next = new URLSearchParams(searchParams);
    if (favouritesOnly) next.delete("favourites");
    else next.set("favourites", "1");
    setSearchParams(next, { replace: true });
    setPage(0);
  }

  function handleFilterChange(f: CarFilter) {
    // filterToParams only emits filter axes — re-append the active sort or a
    // filter tweak would silently reset the ordering.
    const next = filterToParams(f);
    writeSortParams(next, sortBy, sortDir);
    setSearchParams(next, { replace: true });
  }

  // Always scope the request to the active brand. The user's chosen filter
  // never overrides this — brand selection is a navigation concern.
  const scopedFilter = useMemo<CarFilter>(
    () => ({ ...filter, brand_codes: [activeBrand] }),
    [filter, activeBrand],
  );

  const brandLabel = useMemo(() => {
    const match = brands.find((b) => b.brand_code === activeBrand);
    return match?.display_name ?? activeBrand;
  }, [brands, activeBrand]);

  // Effective visibility = user preference AND data gate. The user can hide
  // a data-gated column where data exists, but cannot force-show one on a
  // brand with no data (it would render a column of dashes — e.g. structure
  // week is Polestar-only).
  const visibleColumns = useMemo(
    () =>
      COLUMNS.filter((col) => {
        const wanted =
          col.lockedVisible === true || (columnPrefs[col.id] ?? col.defaultVisible);
        const hasData = col.dataGate ? col.dataGate(cars) : true;
        const authed = col.requiresAuth !== true || user != null;
        return wanted && hasData && authed;
      }),
    [cars, columnPrefs, user],
  );

  // Any filter/brand change resets to the first page; otherwise an out-of-range
  // offset would return an empty page after narrowing the result set.
  useEffect(() => {
    setPage(0);
  }, [scopedFilter]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getCars(scopedFilter, {
      limit: pageSize,
      offset: page * pageSize,
      sort_by: sortBy,
      sort_dir: sortDir,
      favourites_only: favouritesOnly,
    })
      .then((resp) => {
        if (!cancelled) {
          setCars(resp.cars);
          setTotal(resp.total);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scopedFilter, page, pageSize, sortBy, sortDir, favouritesOnly]);

  return (
    <div className="flex min-h-screen">
      <FilterSidebar
        filter={filter}
        onChange={handleFilterChange}
        brandCode={activeBrand}
      />

      <main className="flex-1 p-3 sm:p-6 min-w-0">
        <h1 className="text-2xl font-bold mb-1">{brandLabel} Preowned</h1>

        {loading && <p className="text-gray-500 mt-4">Loading…</p>}

        {error && (
          <p className="mt-4 text-red-600 bg-red-50 border border-red-200 rounded p-3">
            Error: {error}
          </p>
        )}

        {!loading && !error && (
          <>
            <div className="flex items-end justify-between mb-4 gap-3">
              <p className="text-sm text-gray-400">
                {total > 0
                  ? `Showing ${page * pageSize + 1}–${page * pageSize + cars.length} of ${total.toLocaleString("en-GB")} car${total !== 1 ? "s" : ""}`
                  : "0 cars"}
              </p>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-500">
                  Rows{" "}
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(0);
                    }}
                    className="ml-1 border rounded px-1.5 py-1 text-sm text-gray-600 bg-white"
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
                {user && (
                  <button
                    type="button"
                    onClick={toggleFavouritesOnly}
                    aria-pressed={favouritesOnly}
                    title={
                      favouritesOnly
                        ? "Show all cars"
                        : "Show only cars you have starred"
                    }
                    className={`px-3 py-1.5 rounded border text-sm ${
                      favouritesOnly
                        ? "bg-amber-50 border-amber-300 text-amber-700"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    ★ Favourites
                  </button>
                )}
                <div className="relative">
                <button
                  type="button"
                  onClick={() => setChooserOpen((o) => !o)}
                  className="px-3 py-1.5 rounded border text-sm text-gray-600 hover:bg-gray-50"
                >
                  Columns
                </button>
                {chooserOpen && (
                  <>
                    {/* Transparent backdrop: any outside click closes. */}
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setChooserOpen(false)}
                    />
                    <div className="absolute right-0 mt-1 z-20 w-52 bg-white border rounded shadow-lg p-3 text-sm">
                      {COLUMNS.filter((col) => !col.lockedVisible).map((col) => {
                        const noData = col.dataGate ? !col.dataGate(cars) : false;
                        const checked =
                          !noData && (columnPrefs[col.id] ?? col.defaultVisible);
                        return (
                          <label
                            key={col.id}
                            className={`flex items-center gap-2 py-0.5 ${
                              noData ? "text-gray-300" : "text-gray-700"
                            }`}
                            title={
                              noData ? "No data for this brand" : undefined
                            }
                          >
                            <input
                              type="checkbox"
                              disabled={noData}
                              checked={checked}
                              onChange={() =>
                                setColumnPrefs((prefs) => ({
                                  ...prefs,
                                  [col.id]: !(prefs[col.id] ?? col.defaultVisible),
                                }))
                              }
                            />
                            {col.label}
                          </label>
                        );
                      })}
                      <button
                        type="button"
                        className="mt-2 text-blue-600 hover:underline"
                        onClick={() => setColumnPrefs({})}
                      >
                        Reset to defaults
                      </button>
                    </div>
                  </>
                )}
                </div>
              </div>
            </div>
            <div className="overflow-x-auto rounded border">
              <table className="w-full table-fixed text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    {visibleColumns.map((col) => {
                      const sortable = col.sortKey !== undefined;
                      const active = sortable && col.sortKey === sortBy;
                      const arrow = active ? (sortDir === "asc" ? " ▲" : " ▼") : "";
                      return (
                        <th
                          key={col.id}
                          style={{ width: colWidths[col.id] ?? col.defaultWidth }}
                          className={`relative ${col.responsiveCls} px-2 sm:px-4 py-2 text-left text-xs font-semibold uppercase ${
                            sortable ? "cursor-pointer select-none" : ""
                          } ${active ? "text-blue-700" : "text-gray-500"}`}
                          title={sortable ? `Sort by ${col.label}` : undefined}
                          onClick={
                            sortable ? () => toggleSort(col.sortKey!) : undefined
                          }
                        >
                          {col.label}
                          {arrow}
                          {/* Drag grip on the right edge; clicks must not
                              bubble into the sort toggle. */}
                          <span
                            data-testid={`resize-${col.id}`}
                            aria-hidden="true"
                            onMouseDown={(e) => startColumnResize(e, col)}
                            onClick={(e) => e.stopPropagation()}
                            className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-blue-200"
                          />
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {cars.map((car) => (
                    <CarRow key={car.car_id} car={car} columns={visibleColumns} />
                  ))}
                  {cars.length === 0 && (
                    <tr>
                      <td
                        colSpan={visibleColumns.length}
                        className="px-4 py-8 text-center text-gray-400"
                      >
                        {favouritesOnly
                          ? "No starred cars match your filter."
                          : "No cars match your filter."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {total > pageSize && (
              <div className="flex items-center justify-between mt-4 text-sm">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-1.5 rounded border disabled:opacity-40 hover:bg-gray-50"
                >
                  ← Previous
                </button>
                <span className="text-gray-500">
                  Page {page + 1} of {Math.ceil(total / pageSize)}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={(page + 1) * pageSize >= total}
                  className="px-3 py-1.5 rounded border disabled:opacity-40 hover:bg-gray-50"
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
